
import _ from 'lodash';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { setObjectFit } from '../../base/conference';
import { isToolbarButtonEnabled } from '../../base/config/functions.web';
import { openDialog } from '../../base/dialog';
import { isDeviceLandScape } from '../../base/environment/environment';
import {
    IconChat,
    IconCloseCircle,
    IconContain,
    IconCover,
    IconCrown,
    IconMicDisabled,
    IconModeratorPin,
    IconMuteEveryoneElse,
    IconMuteVideoEveryoneElse,
    IconRaisedHandSlash,
    IconUserGroup,
    IconUserPinned,
    IconVideoOff,
    IconVolume
} from '../../base/icons';
import {
    PARTICIPANT_ROLE,
    SET_MODERATOR_PIN,
    getLocalParticipant,
    isLocalParticipantModerator,
    isParticipantModerator,
    participantVolumeChanged,
    pinParticipant
} from '../../base/participants';
import { getBrandingColorPalette } from '../../base/styles';
import {
    getIsParticipantAudioMuted,
    getIsParticipantVideoMuted
} from '../../base/tracks';
import SendToRoomButton from '../../breakout-rooms/components/web/SendToRoomButton';
import { PARTICIPANT_MENU_BUTTONS as BUTTONS } from '../../breakout-rooms/constants';
import {
    getBreakoutRooms,
    getCurrentRoomId
} from '../../breakout-rooms/functions';
import { openChat } from '../../chat/actions';
import { setIsPollsTabFocused } from '../../chat/actions.any';
import { pollsTabFocusedHandler } from '../../conference/actions.web';
import { setLargeVideoFullscreen } from '../../large-video/actions.any';
import { NOTIFICATION_TYPE, showNotification } from '../../notifications';
import {
    ForceMuteRemoteDialog,
    ForceMuteRemoteParticipantVideoDialog,
    GrantModeratorDialog,
    KickRemoteParticipantDialog,
    MuteEveryoneDialog,
    MuteEveryonesVideoDialog
} from '../../video-menu';
import { VolumeControlDialog } from '../../video-menu/components/web';
import DemoteToVisitorDialog from '../../video-menu/components/web/DemoteToVisitorDialog';
import MuteRemoteParticipantsVideoDialog from '../../video-menu/components/web/MuteRemoteParticipantsVideoDialog';
import { iAmVisitor } from '../../visitors/functions';
import { close, closeParticipantsPaneModal } from '../actions';
import {
    clearRaisedHandByParticipant,
    getComputedOuterHeight
} from '../functions';

import {
    ContextMenu,
    ContextMenuIcon,
    ContextMenuItem,
    ContextMenuItemGroup
} from './styled';


const _inlineStyles = {
    _s0: { padding: '10px 16px' }
};

declare var config: object;

type Props = {

  /**
   * Callback used to open a confirmation dialog for audio muting.
   */
  muteAudio: Function,

  /**
   * Target elements against which positioning calculations are made.
   */
  offsetTarget: HTMLElement,

  /**
   * Callback for the mouse entering the component.
   */
  onEnter: Function,

  /**
   * Callback for the mouse leaving the component.
   */
  onLeave: Function,

  /**
   * Callback for making a selection in the menu.
   */
  onSelect: Function,

  /**
   * Participant reference.
   */
  participant: Object,
};

export const MeetingParticipantContextMenu = ({
    offsetTarget,
    onEnter,
    onLeave,
    onSelect,
    muteAudio,
    participant
}: Props) => {
    const dispatch = useDispatch();
    const containerRef = useRef(null);
    const participants = useSelector(state => state['features/base/participants']);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const totalModerators = participants.filter(it => it.role === 'moderator' && !it?.isFakeParticipant);
    const isMainModerator = useSelector(state => state['features/base/jwt']?.user?.moderator || state['features/base/jwt']?.moderator);
    const isPrivateChatDisabled = useSelector(state => state['features/chat']?.isPrivateChatDisabled);
    const moderators_allowed = useSelector(state => state['features/base/mtApi']?.pregData?.moderators_allowed);
    const { objectFit } = useSelector(state => state['features/base/conference']);
    const { largevideofullscreen } = useSelector(state => state['features/large-video']);
    const lockaudio = useSelector(
    state => state['features/base/conference']?.lockAudio
    );
    const visibleButtons = useSelector(state => state['features/base/config'].interfaceConfig?.TOOLBAR_BUTTONS);
    const interfaceConfig = useSelector(state => state['features/base/config'].interfaceConfig);
    const _visibleButtons = new Set(visibleButtons);
    const lockvideo = useSelector(
    state => state['features/base/conference']?.lockVideo
    );
    const isChatButtonEnabled = useSelector(isToolbarButtonEnabled('chat'));
    const isParticipantVideoMuted = useSelector(
    getIsParticipantVideoMuted(participant)
    );
    const visitorsMode = useSelector(state => iAmVisitor(state));
    const visitorsSupported = false; // useSelector(state => state['features/visitors'].supported);
    const isParticipantAudioMuted = useSelector(
    getIsParticipantAudioMuted(participant)
    );
    const [ isHidden, setIsHidden ] = useState(true);
    const { jwt = null } = useSelector(state => state['features/base/jwt']);
    const { t } = useTranslation();
    const isPersonModerator = isParticipantModerator(participant);
    const rooms = useSelector(getBreakoutRooms);
    const conference = useSelector(state => state['features/base/conference'].conference);
    const state1 = useSelector(state => state);
    const menuStyle = { color: interfaceConfig?.applyMeetingSettings ? `${getBrandingColorPalette(interfaceConfig)?.primary} !important` : '' };

    useLayoutEffect(() => {
        if (
            participant
      && containerRef.current
      && offsetTarget?.offsetParent
      && offsetTarget.offsetParent instanceof HTMLElement
        ) {
            const { current: container } = containerRef;
            const {
                offsetTop,
                offsetParent: { offsetHeight, scrollTop }
            } = offsetTarget;
            const outerHeight = getComputedOuterHeight(container);

            const wouldOverflowBottom = offsetTop + outerHeight > offsetHeight + scrollTop + 100;

            // If it overflows below, flip above the target; never go negative off-screen.
            container.style.top = wouldOverflowBottom
                ? `${Math.max(offsetTop - outerHeight, 0)}px`
                : `${offsetTop}px`;

            setIsHidden(false);
        } else {
            setIsHidden(true);
        }
    }, [ participant, offsetTarget ]);

    const grantModerator = useCallback(() => {

        if (_.isNil(jwt)) {
            return;
        }
        if (participant.role === PARTICIPANT_ROLE.MODERATOR && totalModerators.length >= moderators_allowed) {
            dispatch(
            openDialog(GrantModeratorDialog, {
                participantID: participant.id
            })
            );

            return;
        } else if (totalModerators.length >= moderators_allowed) {
            dispatch(showNotification({
                descriptionArguments: { number: moderators_allowed },
                descriptionKey: 'notify.maxGrantModeratorDes',
                titleKey: 'notify.maxGrantModerator',
                customActionNameKey: 'notify.upgradeNow',
                customActionHandler: () => window.open(`${config?.URL_PREFIX}updateSubscription`, '_new')
            }));

            return;
        }

        dispatch(
            openDialog(GrantModeratorDialog, {
                participantID: participant.id
            })
        );
    }, [ dispatch, participant ]);

    const kick = useCallback(() => {
        dispatch(
      openDialog(KickRemoteParticipantDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const muteEveryoneElse = useCallback(() => {
        dispatch(
      openDialog(MuteEveryoneDialog, {
          exclude: [ participant.id ]
      })
        );
    }, [ dispatch, participant ]);

    const muteEveryoneElseVideo = useCallback(() => {
        dispatch(
            openDialog(MuteEveryonesVideoDialog, {
                exclude: [ participant.id ]
            })
        );
    }, [ dispatch, participant ]);

    const muteVideo = useCallback(() => {
        dispatch(
      openDialog(MuteRemoteParticipantsVideoDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const participantModeratorPin = useCallback(() => {
        if (!conference) {

            return;
        }

        const myId = conference.myUserId();
        const isModeratorPinned = participant && participant.moderatorPin;
        const pinAction = isModeratorPinned ? 'unpin' : 'pin';

        conference.sendMessage(
            {
                type: SET_MODERATOR_PIN,
                participant_id: participant.id,
                senderId: myId,
                action: pinAction
            }
        );


        // Show notification
        const notificationKey = pinAction === 'pin'
            ? t('videothumbnail.pinByModerator')
            : t('videothumbnail.unpinByModerator');

        onLeave();

        dispatch(
                showNotification(
                    {
                        appearance: NOTIFICATION_TYPE.NORMAL,
                        titleKey: notificationKey,
                        descriptionKey: t('videothumbnail.moderatorPinDiscription')
                    },
                    3000
                )
        );
    }, [ dispatch, participant, conference, onLeave ]);

    const handleobjectfit = useCallback(() => {

        dispatch(setObjectFit(
            objectFit === 'cover' ? 'contain' : 'cover'
        ));
        onLeave();
    }, [ dispatch, participant, onLeave ]);

    const forceMuteVideo = useCallback(() => {
        dispatch(
      openDialog(ForceMuteRemoteParticipantVideoDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const volumeControl = useCallback(() => {
        dispatch(openDialog(VolumeControlDialog, {
            participantID: participant.id,
            initialVolumeValue: participant?.volume,
            onVolumeChange: (id, volume) => dispatch(participantVolumeChanged(id, volume))
        }));
    }, [ dispatch, participant ]);

    const forceMuteAudio = useCallback(() => {
        dispatch(
      openDialog(ForceMuteRemoteDialog, {
          participantID: participant.id
      })
        );
    }, [ dispatch, participant ]);

    const participantPin = useCallback(() => {
        dispatch(pinParticipant(participant.id));
        onLeave();

    }, [ dispatch, participant, onLeave ]);

    const handleLargeVideoFullscreen = useCallback(() => {
        dispatch(pinParticipant(participant.id, true));
        dispatch(setLargeVideoFullscreen(!largevideofullscreen));
        onLeave();
    }, [ dispatch, participant, largevideofullscreen, onLeave ]);

    // const forceMuteAudioeveryOne = useCallback(() => {
    //     dispatch(openDialog(ForceMuteEveryoneDialog, {
    //         participantID: participant.id
    //     }));
    // }, [ dispatch, participant ]);


    const onDemoteParticipant = useCallback(() => {
        const participantID = participant.id;

        dispatch(openDialog(DemoteToVisitorDialog, { participantID }));
    }, [ participant?.id ]);

    const sendPrivateMessage = useCallback(() => {
        dispatch(openChat(participant));
        dispatch(closeParticipantsPaneModal());
        dispatch(close());
        dispatch(pollsTabFocusedHandler(false));
        dispatch(setIsPollsTabFocused(false));
    }, [ dispatch, participant ]);

    const onBreakoutRoomButtonClick = () => {
    // DO something
    };
    const _currentRoomId = getCurrentRoomId(state1);
    const localParticipant = getLocalParticipant(state1);

    const _isModerator = Boolean(
    localParticipant?.role === PARTICIPANT_ROLE.MODERATOR
    );

    const _rooms = Object.values(rooms);
    const breakoutRoomsButtons = [];

    const getButtonProps = key => {
        return {
            key,
            participantID: participant?.id
        };
    };

    if (_isModerator) {
        _rooms.forEach(room => {
            if (room.id !== _currentRoomId) {
                breakoutRoomsButtons.push(
          <SendToRoomButton
              { ...getButtonProps(BUTTONS.SEND_PARTICIPANT_TO_ROOM) }
              key = { room.id }
              onClick = { onBreakoutRoomButtonClick }
              room = { room } />
                );
            }
        });
    }

    if (!participant) {
        return null;
    }

    return (
        <>
            <ContextMenu
                className = { 'ignored-child meeting-participant-context-menu' }
                innerRef = { containerRef }
                isHidden = { isHidden }
                onClick = { onSelect }
                onMouseEnter = { onEnter }
                onMouseLeave = { onLeave }>
                {isLocalModerator && (
                    <ContextMenuItemGroup background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }>
                        <>
                            {!isParticipantAudioMuted && (
                                <ContextMenuItem
                                    background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                                    color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                    onClick = { muteAudio(participant.id) }>
                                    <ContextMenuIcon
                                        fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                        src = { IconMicDisabled } />
                                    <span style = { menuStyle }>{t('dialog.muteParticipantButton')}</span>
                                </ContextMenuItem>
                            )}
                            <ContextMenuItem
                                background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                                color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                onClick = { muteEveryoneElse }>
                                <ContextMenuIcon
                                    fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                    src = { IconMuteEveryoneElse } />
                                <span style = { menuStyle }>{t('toolbar.accessibilityLabel.muteEveryoneElse')}</span>
                            </ContextMenuItem>
                            <ContextMenuItem
                                background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                                color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                onClick = { muteEveryoneElseVideo }>
                                <ContextMenuIcon
                                    fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                    src = { IconMuteVideoEveryoneElse } />
                                <span style = { menuStyle }>{t('videothumbnail.domuteVideoOfOthers')}</span>
                            </ContextMenuItem>
                        </>
                    </ContextMenuItemGroup>
                )}
                <ContextMenuItemGroup background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }>
                    {isMainModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            disable = { _.isNil(jwt) }
                            onClick = { grantModerator }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconCrown } />
                            <span style = { menuStyle }>
                                {isPersonModerator
                                    ? t('dialog.revokeModerationTitle')
                                    : t('toolbar.accessibilityLabel.grantModerator')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator
            && (isParticipantVideoMuted || (
                <ContextMenuItem
                    background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                    color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                    onClick = { muteVideo }>
                    <ContextMenuIcon
                        fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        src = { IconVideoOff } />
                    <span style = { menuStyle }>{t('participantsPane.actions.stopVideo')}</span>
                </ContextMenuItem>
            ))}
                    {isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { forceMuteVideo }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconVideoOff } />
                            <span style = { menuStyle }>
                                {lockvideo[`${participant?.id}`] !== undefined
                && lockvideo[`${participant?.id}`] === true
                                    ? t('participantsPane.actions.unForceMuteVideo')
                                    : t('participantsPane.actions.forceMuteVideo')}
                            </span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { forceMuteAudio }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconMicDisabled } />
                            <span style = { menuStyle }>
                                {lockaudio[`${participant?.id}`] !== undefined
                && lockaudio[`${participant?.id}`] === true
                                    ? t('participantsPane.actions.unForceMute')
                                    : t('participantsPane.actions.forceMute')}
                            </span>
                        </ContextMenuItem>
                    )}
                    <ContextMenuItem
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                        color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        onClick = { volumeControl }>
                        <ContextMenuIcon
                            fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            src = { IconVolume } />
                        <span style = { menuStyle }>{t('volumeSlider.label')}</span>
                    </ContextMenuItem>
                    {_visibleButtons.has('pin-user') && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { participantPin }>
                            <ContextMenuIcon
                                src = { IconUserPinned }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' } />
                            <span style = { menuStyle }>{participant?.pinned ? t('videothumbnail.unpinMe') : t('videothumbnail.pinMe')}</span>
                        </ContextMenuItem>
                    )}
                    {(window.innerWidth < 500 || isDeviceLandScape()) && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { handleLargeVideoFullscreen }>
                            <ContextMenuIcon
                                src = { IconUserPinned }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' } />
                            <span style = { menuStyle }>{largevideofullscreen ? t('videothumbnail.exitLargeVideoFullscreen') : t('videothumbnail.makeLargeVideoFullscreen')}</span>
                        </ContextMenuItem>
                    )}
                    {isLocalModerator && _visibleButtons.has('moderator-pin') && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { participantModeratorPin }>
                            <ContextMenuIcon
                                src = { IconModeratorPin }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' } />
                            <span style = { menuStyle }>{participant?.moderatorPin ? t('videothumbnail.unpinEveryone') : t('videothumbnail.pinEveryone')}</span>
                        </ContextMenuItem>
                    )}
                    {_visibleButtons.has('fit-video') && <ContextMenuItem
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                        color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                        onClick = { handleobjectfit }>
                        <ContextMenuIcon
                            fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            src = { objectFit === 'cover' ? IconContain : IconCover } />
                        <span style = { menuStyle }>{objectFit === 'cover' ? t('tooltip.switchToContain') : t('tooltip.switchToCover')}</span>
                    </ContextMenuItem>}
                    {isLocalModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { kick }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconCloseCircle } />
                            <span style = { menuStyle }>{t('videothumbnail.kick')}</span>
                        </ContextMenuItem>
                    )}
                    {visitorsSupported && _isModerator && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { onDemoteParticipant }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconUserGroup } />
                            <span style = { menuStyle }>Demote to Visitor</span>
                        </ContextMenuItem>
                    )}
                    {_isModerator && participant.raisedHand && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { () => clearRaisedHandByParticipant(participant) }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '#fff' }
                                src = { IconRaisedHandSlash }
                                strokeColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '#fff' } />
                            <span style = { menuStyle }>{t('participantsPane.actions.clearRaisedHand')}</span>
                        </ContextMenuItem>
                    )}
                    {isChatButtonEnabled && !visitorsMode && !isPrivateChatDisabled && (
                        <ContextMenuItem
                            background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '' }
                            color = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                            onClick = { sendPrivateMessage }>
                            <ContextMenuIcon
                                fillColor = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }
                                src = { IconChat } />
                            <span style = { menuStyle }>{t('toolbar.accessibilityLabel.privateMessage')}</span>
                        </ContextMenuItem>
                    )}
                </ContextMenuItemGroup>
                {breakoutRoomsButtons.length > 0 && (
                    <ContextMenuItemGroup
                        background = { interfaceConfig?.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.primary : '' }>
                        <div style = { _inlineStyles._s0 }>
                            {t('breakoutRooms.actions.sendToBreakoutRoom')}
                        </div>
                        {breakoutRoomsButtons}
                    </ContextMenuItemGroup>
                )}
            </ContextMenu>
        </>
    );
};
